import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Search, Download, Eye, FileText } from 'lucide-react';
import { useState } from 'react';
import { getInvoices, getCompanies } from '../../services/storage';
import type { Invoice } from '../../types';

export const Invoices = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [invoices] = useState<Invoice[]>(getInvoices());
  const companies = getCompanies();

  const filteredInvoices = invoices.filter(invoice => {
    const company = companies.find(c => c.id === invoice.companyId);
    return (
      invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getStatusBadge = (status: Invoice['status']) => {
    const variants = {
      paid: 'success',
      pending: 'secondary',
      overdue: 'default',
      canceled: 'default'
    } as const;
    
    const labels = {
      paid: 'Pago',
      pending: 'Pendente',
      overdue: 'Vencido',
      canceled: 'Cancelado'
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Faturas | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Faturas</h1>
            <p className="text-neutral-600">Gerencie todas as faturas do sistema</p>
          </div>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Exportar
          </Button>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              placeholder="Buscar faturas..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Invoices Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Número</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Empresa</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Descrição</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Valor</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Vencimento</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {filteredInvoices.map((invoice) => {
                  const company = companies.find(c => c.id === invoice.companyId);
                  return (
                    <tr key={invoice.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4 font-medium text-neutral-900">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 text-neutral-600">{company?.name}</td>
                      <td className="px-6 py-4 text-neutral-600">{invoice.description}</td>
                      <td className="px-6 py-4 font-medium text-neutral-900">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(invoice.amount)}
                      </td>
                      <td className="px-6 py-4 text-neutral-600">
                        {new Date(invoice.dueDate).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-neutral-400 hover:text-primary-blue transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-neutral-400 hover:text-primary-blue transition-colors">
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};
